import React from 'react'
import { ApiError, FormField, FormFieldType, FormTemplate, Service, ValidationError, api } from '../../api/client'
import FormView from '../../forms/FormView'
import { isAnswerable } from '../../forms/fields'
import Field from './Field'
import {
  FIELD_TYPES,
  FormDraft,
  addField,
  isDirty,
  moveField,
  newOption,
  removeField,
  toTemplateInput,
  typeLabel,
  updateField,
} from './formEditorDraft'
import { GroupContext } from './SettingsGroup'
import { saveErrorMessage } from './toast'
import { useDialog } from '../../hooks/useDialog'
import { BackLink, IconButton } from '../../ui/Icon'
import '../../forms/forms.css'
import './Settings.css'

interface Props {
  // The saved template, or null for a new one.
  template: FormTemplate | null
  // Where the draft starts: the saved fields, a starter's, or empty.
  initial: FormDraft
  services: Service[]
  onClose: () => void
  onSaved: (message: string) => void
}

export default function FormEditor({ template, initial, services, onClose, onSaved }: Props) {
  const group = React.useContext(GroupContext)
  const [draft, setDraft] = React.useState<FormDraft>(initial)
  const [open, setOpen] = React.useState<string | null>(null) // id of the field being edited
  const [adding, setAdding] = React.useState(false)
  const [previewing, setPreviewing] = React.useState(false)
  const [askEarlier, setAskEarlier] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [confirmArchive, setConfirmArchive] = React.useState(false)

  const dirty = isDirty(initial, draft)
  React.useEffect(() => {
    group?.setUnsaved('Consultation forms', dirty)
    return () => group?.setUnsaved('Consultation forms', false)
  }, [group, dirty])

  const leave = () => {
    if (dirty && !window.confirm('Leave without saving? Your changes will be lost.')) return
    onClose()
  }

  const setFields = (fields: FormField[]) => setDraft((d) => ({ ...d, fields }))
  const patch = (index: number, p: Partial<FormField>) => setFields(updateField(draft.fields, index, p))

  const add = (type: FormFieldType) => {
    const fields = addField(draft.fields, type)
    setFields(fields)
    setOpen(fields[fields.length - 1].id)
    setAdding(false)
  }

  const publish = async () => {
    setBusy(true)
    setErrors({})
    setError('')
    try {
      if (template) {
        await api.updateFormTemplate(template.id, toTemplateInput(draft, askEarlier))
      } else {
        await api.createFormTemplate(toTemplateInput(draft))
      }
      onSaved(`${draft.name.trim()} published.`)
    } catch (err) {
      if (err instanceof ValidationError) {
        setErrors(err.fields)
        // Open the first question with a problem so she can see it.
        const bad = Object.keys(err.fields).map((k) => /^fields\.(\d+)\./.exec(k)?.[1]).find((i) => i !== undefined)
        if (bad !== undefined && draft.fields[Number(bad)]) setOpen(draft.fields[Number(bad)].id)
      } else {
        setError(saveErrorMessage(err))
      }
      setBusy(false)
    }
  }

  // The services using this form, by name.
  const usedBy = (template?.service_ids ?? [])
    .map((id) => services.find((s) => s.id === id)?.name)
    .filter((n): n is string => !!n)

  const archive = async () => {
    if (!template) return
    if (usedBy.length > 0) {
      setError(inUseMessage(usedBy))
      return
    }
    if (!confirmArchive) {
      setConfirmArchive(true)
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.archiveFormTemplate(template.id)
      onSaved(`${template.name} archived.`)
    } catch (err) {
      if (err instanceof ApiError && err.problem === 'form-in-use') {
        // Someone attached it since the list loaded; look again.
        api.getFormTemplate(template.id).then(
          (t) => setError(inUseMessage(namesOf(t.service_ids, services))),
          () => setError(inUseMessage([])),
        )
      } else {
        setError(saveErrorMessage(err))
      }
      setConfirmArchive(false)
      setBusy(false)
    }
  }

  const title = template ? 'Edit form' : 'New form'
  const back = previewing ? () => setPreviewing(false) : leave
  // A modal over the settings group: Escape goes back as the back link does,
  // and focus stays inside it.
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, back, { closable: !busy })

  return (
    <div ref={dialogRef} className="settings-panel form-editor" role="dialog" aria-modal="true" aria-label={title}>
      <div className="menu-header">
        {previewing ? (
          <BackLink label="Back to editing" onClick={back}>Editing</BackLink>
        ) : (
          <BackLink onClick={back}>Consultation forms</BackLink>
        )}
      </div>
      <div className="settings-body">
        <h2 className="menu-title">{previewing ? 'Preview' : title}</h2>

        {previewing ? (
          <>
            <p className="settings-intro">This is what your client sees. Nothing is saved from here.</p>
            {draft.fields.length === 0 ? (
              <p className="settings-status">Add a question to see the form.</p>
            ) : (
              <FormView fields={draft.fields} answers={{}} onChange={() => {}} readOnly />
            )}
          </>
        ) : (
          <div className="settings-form">
            <Field id="form-name" label="Name" error={errors.name}>
              <input id="form-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={draft.drawn_signature}
                onChange={(e) => setDraft({ ...draft, drawn_signature: e.target.checked })}
              />
              Clients draw their signature
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={draft.guardian_under_18}
                onChange={(e) => setDraft({ ...draft, guardian_under_18: e.target.checked })}
              />
              Clients under 18 need a parent or guardian
            </label>

            <h3 className="settings-section-label">Questions</h3>
            {errors.fields && <p className="field-error" role="alert">{errors.fields}</p>}
            {draft.fields.length === 0 ? (
              <p className="settings-status">No questions yet. Add your first one below.</p>
            ) : (
              <ul className="service-list form-fields">
                {draft.fields.map((f, i) => (
                  <li key={f.id} className={f.type === 'heading' ? 'form-editor__item form-editor__item--heading' : 'form-editor__item'}>
                    <div className="form-editor__row">
                      <div className="form-editor__summary">
                        <span className="service-item__name">{f.label.trim() || (f.type === 'heading' ? 'Untitled section' : 'Untitled')}</span>
                        <span className="service-item__meta">
                          {typeLabel(f.type)}
                          {isAnswerable(f) && (f.required || f.type === 'statement') ? ' · Required' : ''}
                          {f.health ? ' · Health' : ''}
                        </span>
                      </div>
                      <span className="move-buttons">
                        <IconButton icon="arrow-up" size={18} className="move-button" label={`Move ${f.label || 'question'} up`} disabled={i === 0} onClick={() => setFields(moveField(draft.fields, i, -1))} />
                        <IconButton icon="arrow-down" size={18} className="move-button" label={`Move ${f.label || 'question'} down`} disabled={i === draft.fields.length - 1} onClick={() => setFields(moveField(draft.fields, i, 1))} />
                      </span>
                    </div>
                    <div className="form-editor__actions">
                      <button type="button" className="btn btn-secondary btn-compact" aria-expanded={open === f.id} onClick={() => setOpen(open === f.id ? null : f.id)}>
                        {open === f.id ? 'Done' : 'Edit'}
                      </button>
                      <button type="button" className="btn btn-danger btn-compact" onClick={() => { setFields(removeField(draft.fields, i)); if (open === f.id) setOpen(null) }}>
                        Delete
                      </button>
                    </div>
                    {open === f.id && <FieldEditor field={f} index={i} errors={errors} onChange={(p) => patch(i, p)} />}
                  </li>
                ))}
              </ul>
            )}

            {adding ? (
              <div className="chip-group" role="group" aria-label="Question type">
                {FIELD_TYPES.map((t) => (
                  <button key={t.type} type="button" className="chip" onClick={() => add(t.type)}>{t.label}</button>
                ))}
                <button type="button" className="chip chip--add" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            ) : (
              <button type="button" className="btn btn-secondary btn-block" onClick={() => setAdding(true)}>Add question</button>
            )}

            {template && (
              <label className="checkbox-row">
                <input type="checkbox" checked={askEarlier} onChange={(e) => setAskEarlier(e.target.checked)} />
                Also ask clients who signed an earlier version
              </label>
            )}

            {error && <p className="field-error" role="alert">{error}</p>}

            <div className="sheet-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setPreviewing(true)} disabled={busy}>Preview</button>
              <button type="button" className="btn btn-primary" onClick={publish} disabled={busy}>
                {busy ? 'Publishing…' : 'Publish'}
              </button>
            </div>

            {template && (
              <button type="button" className="btn btn-danger btn-compact" onClick={archive} disabled={busy}>
                {confirmArchive ? `Tap again to archive ${template.name}` : 'Archive form'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const namesOf = (ids: string[], services: Service[]) =>
  ids.map((id) => services.find((s) => s.id === id)?.name).filter((n): n is string => !!n)

function inUseMessage(names: string[]): string {
  const where = names.length > 0 ? names.join(', ') : 'a service'
  return `This form is used by ${where}. Remove it from ${names.length === 1 ? 'that service' : 'those services'} first, then archive it.`
}

function FieldEditor({ field, index, errors, onChange }: { field: FormField; index: number; errors: Record<string, string>; onChange: (p: Partial<FormField>) => void }) {
  const key = `fields.${index}`
  const base = `form-field-${field.id}`
  const hasOptions = field.type === 'checklist' || field.type === 'choice'
  const setOption = (oi: number, label: string) =>
    onChange({ options: field.options.map((o, i) => (i === oi ? { ...o, label } : o)) })
  return (
    <div className="form-editor__edit">
      <Field id={`${base}-label`} label={field.type === 'text' ? 'Text' : field.type === 'heading' ? 'Section heading' : 'Question'} error={errors[`${key}.label`]}>
        {field.type === 'text' ? (
          <textarea id={`${base}-label`} rows={4} value={field.label} onChange={(e) => onChange({ label: e.target.value })} />
        ) : (
          <input id={`${base}-label`} value={field.label} onChange={(e) => onChange({ label: e.target.value })} />
        )}
      </Field>
      {errors[`${key}.type`] && <p className="field-error" role="alert">{errors[`${key}.type`]}</p>}
      {isAnswerable(field) && field.type !== 'statement' && (
        <label className="checkbox-row">
          <input type="checkbox" checked={field.required} onChange={(e) => onChange({ required: e.target.checked })} />
          Required
        </label>
      )}
      {isAnswerable(field) && (
        <label className="checkbox-row">
          <input type="checkbox" checked={field.health} onChange={(e) => onChange({ health: e.target.checked })} />
          Health question
        </label>
      )}
      {field.type === 'yes_no' && (
        <label className="checkbox-row">
          <input type="checkbox" checked={field.details} onChange={(e) => onChange({ details: e.target.checked })} />
          Ask for details on a yes
        </label>
      )}
      {field.type === 'checklist' && (
        <label className="checkbox-row">
          <input type="checkbox" checked={field.none_option} onChange={(e) => onChange({ none_option: e.target.checked })} />
          Offer "None of these"
        </label>
      )}
      {hasOptions && (
        <Field id={`${base}-option-0`} label="Options" error={errors[`${key}.options`]}>
          <div className="form-editor__options">
            {field.options.map((o, oi) => (
              <div key={o.id} className="form-editor__option">
                <input
                  id={`${base}-option-${oi}`}
                  aria-label={`Option ${oi + 1}`}
                  value={o.label}
                  onChange={(e) => setOption(oi, e.target.value)}
                />
                <IconButton
                  icon="close"
                  size={18}
                  className="remove-button"
                  label={`Remove option ${oi + 1}`}
                  disabled={field.options.length === 1}
                  onClick={() => onChange({ options: field.options.filter((_, i) => i !== oi) })}
                />
              </div>
            ))}
            <button type="button" className="btn btn-quiet btn-compact" onClick={() => onChange({ options: [...field.options, newOption()] })}>
              Add option
            </button>
          </div>
        </Field>
      )}
    </div>
  )
}
